// TabContent.js
import React from "react";
import ChartRenderer from "./ChartRenderer";
import "../styles/TabContent.css";
import generateChartData from "../../utils/generateChartData";
import KpiTable from "../KpiTable";
import CrosstabTable from "../../components/CrosstabTable";
import {
  exportPNG,
  exportPDF,
  exportXLSX,
  exportCSV,
  exportPPT,
  flattenCrosstab,
} from "../../utils/DownloadUtils";

export default function TabContent({
  selectedTab,
  updateKPI,
  updateFilter,
  removeFilter,
  removeKPI,
  filteredRows,
  toggleKPIViewMode,
  crosstabDisplayData,
  setCrosstabDisplayData,
}) {
  if (!selectedTab) {
    return (
      <div className="tab-content-empty">
        <p>No tab selected</p>
      </div>
    );
  }

  const safeString = (v, fb = "") => {
    if (typeof v === "string") return v;
    if (typeof v === "number") return String(v);
    if (!v) return fb;
    if (typeof v === "object") {
      return v.Name || v.name || v.label || v.title || fb;
    }
    return fb;
  };
  selectedTab.kpis.map((kpi) => {
    // console.log (kpi.qTitle); // prints "S1 - Your gender?"
    // console.log ("kpi.isCrosstab in TabContent:", kpi.isCrosstab);
    // console.log ("ALL KPIS =>", selectedTab.kpis);
  });

  return (
    <div className="tab-content">
      <div className="tab-content-layout">
        {selectedTab.filters?.length > 0 && (
          <div className="filters-left">
            <h4 className="section-title">🎯 Filters</h4>

            <div className="filters-single-card">
              {selectedTab.filters.map((f) => {
                const filterLabel = safeString(
                  f.label,
                  f.variable_name || "Filter"
                );

                return (
                  <div key={f.id} className="filter-item">
                    <div className="filter-header">
                      <label className="filter-label">{filterLabel}</label>

                      <button
                        className="removefilter-btn"
                        onClick={() => removeFilter(selectedTab.id, f.id)}
                      >
                        ×
                      </button>
                    </div>

                    <select
                      className="filter-select"
                      value={f.selectedValue || ""}
                      onChange={(e) =>
                        updateFilter(selectedTab.id, f.id, e.target.value)
                      }
                    >
                      <option value="">All</option>

                      {f.options?.map((opt, index) => {
                        let optionKey, optionValue, optionLabel;

                        if (typeof opt === "string") {
                          optionKey = optionValue = optionLabel = opt;
                        } else {
                          optionKey = opt.Id || opt.id || index;
                          optionValue =
                            safeString(opt.Value || opt.value || opt.Name) ||
                            `value_${index}`;
                          optionLabel =
                            safeString(opt.Name || opt.name || opt.label) ||
                            `Option ${index + 1}`;
                        }

                        return (
                          <option key={optionKey} value={optionValue}>
                            {optionLabel}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div style={{ flex: 1, overflow: "auto" }}>
          {selectedTab.kpis?.length === 0 ? (
            <div className="tab-content-empty">
              <p>Add KPIs to visualize data.</p>
            </div>
          ) : (
            <div className="kpis-grid">
              {selectedTab.kpis.map((kpi) => {
                const kpiLabel = safeString(
                  kpi.label,
                  kpi.variableName,
                  kpi.qTitle || "KPI"
                );

                // If KPI is numeric (single numeric variable), use the stored average data
                const isNumeric = !!kpi.isNumeric;
                const kpiData = isNumeric
                  ? kpi.data // numeric KPI's data contains a single Average row {name: "Average", value, base}
                  : kpi.data;

                return (
                  <div key={kpi.id} className="kpi-card">
                    <div className="kpi-header">
                      {/* <h5 className="kpi-title">{kpiLabel}</h5> */}
                      <h5 className="kpi-title">
                        {/* {safeString(kpi.label || kpi.variableName, "KPI")} */}
                        {kpi.qTitle && (
                          <span className="kpi-qtile">
                            {safeString(kpi.qTitle)}
                          </span>
                        )}
                      </h5>

                      <button
                        className="remove-btn"
                        onClick={() => removeKPI(selectedTab.id, kpi.id)}
                      >
                        ×
                      </button>
                    </div>

                    <div className="kpi-controls">
                      {!isNumeric && (
                        <>
                          <label className="control-label">Chart Type:</label>

                          <select
                            className="control-select"
                            value={kpi.type}
                            onChange={(e) =>
                              updateKPI(
                                selectedTab.id,
                                kpi.id,
                                "type",
                                e.target.value
                              )
                            }
                          >
                            {kpi.isCrosstab ? (
                              // Crosstab-specific chart types
                              <>
                                <optgroup label="Crosstab Charts">
                                  <option value="stackedcolumn">
                                    Stacked Column
                                  </option>
                                  <option value="stackedbar">
                                    Stacked Bar
                                  </option>
                                  <option value="groupedcolumn">
                                    Grouped Column
                                  </option>
                                  <option value="groupedbar">
                                    Grouped Bar
                                  </option>
                                  <option value="line">Line</option>
                                </optgroup>
                              </>
                            ) : (
                              // Regular KPI chart types
                              <>
                                <optgroup label="Basic Charts">
                                  <option value="bar">Bar</option>
                                  <option value="column">Column</option>
                                  <option value="line">Line</option>
                                  <option value="area">Area</option>
                                </optgroup>

                                <optgroup label="Stacked Charts">
                                  <option value="stackedbar">
                                    Stacked Bar
                                  </option>
                                  <option value="stackedcolumn">
                                    Stacked Column
                                  </option>
                                </optgroup>

                                <optgroup label="Circular Charts">
                                  <option value="pie">Pie</option>
                                  <option value="doughnut">Doughnut</option>
                                  <option value="radialbar">Radial Bar</option>
                                  <option value="radar">Radar</option>
                                </optgroup>

                                <optgroup label="Advanced Charts">
                                  <option value="scatter">Scatter</option>
                                  <option value="composed">Composed</option>
                                  <option value="funnel">Funnel</option>
                                </optgroup>
                              </>
                            )}
                          </select>
                        </>
                      )}
                      {/* Toggle button: hide/disable for numeric KPIs */}
                      {/* <div className="kpi-toggle-wrapper" style={{ marginLeft: "auto" }}>
                        <button
                          className="kpi-toggle-btn"
                          onClick={() => {
                            if (!isNumeric && !kpi.isCrosstab) {
                              toggleKPIViewMode(selectedTab.id, kpi.id);
                            }
                          }}
                          disabled={isNumeric || kpi.isCrosstab}
                          title={
                            isNumeric
                              ? "Numeric KPIs shown as average in table"
                              : kpi.isCrosstab
                                ? "Crosstab KPIs are always shown as table"
                                : ""
                          }
                        >
                          {kpi.isCrosstab ? "📋 Table View" : kpi.viewMode === "chart" ? "📋 Table View" : "📊 Chart View"}
                        </button>
                      </div> */}
                      <div
                        className="kpi-toggle-wrapper"
                        style={{ marginLeft: "auto" }}
                      >
                        <button
                          className="kpi-toggle-btn"
                          onClick={() => {
                            if (!isNumeric) {
                              toggleKPIViewMode(selectedTab.id, kpi.id);
                            }
                          }}
                          disabled={isNumeric}
                          title={
                            isNumeric
                              ? "Numeric KPIs shown as average in table"
                              : ""
                          }
                        >
                          {kpi.viewMode === "chart"
                            ? "📋 Table View"
                            : "📊 Chart View"}
                        </button>
                      </div>

                      <div className="export-buttons">
                        {/* <button className="export-btn" onClick={() => exportPNG(`kpi-${kpi.id}`, `${kpi.label}.png`)}>
                          <i className="ri-image-line"></i>
                          <span className="export-text">PNG</span>
                        </button> */}

                        <button
                          className="export-btn"
                          onClick={() =>
                            exportPDF(`kpi-${kpi.id}`, `${kpi.label}.pdf`)
                          }
                        >
                          <i className="ri-file-pdf-line"></i>
                          <span className="export-text">PDF</span>
                        </button>

                        <button
                          className="export-btn"
                          onClick={() =>
                            exportPPT(
                              `kpi-${kpi.id}`,
                              kpi.label,
                              `${kpi.label}.pptx`
                            )
                          }
                        >
                          <i className="ri-slideshow-line"></i>
                          <span className="export-text">PPT</span>
                        </button>

                        {kpi.viewMode === "table" && (
                          <>
                            {/* <button className="export-btn" onClick={() => exportXLSX(kpi.data, `${kpi.label}.xlsx`)}> */}
                            <button
                              className="export-btn"
                              onClick={() => {
                                const flat = kpi.isCrosstab
                                  ? flattenCrosstab({
                                      rows: kpiData.rowValues,
                                      columns: kpiData.colValues,
                                      matrix: kpiData.table,
                                    })
                                  : kpiData;

                                exportXLSX(flat, `${kpi.label}.xlsx`);
                              }}
                            >
                              <i className="ri-file-excel-line"></i>
                              <span className="export-text">XLSX</span>
                            </button>

                            {/* <button className="export-btn" onClick={() => exportCSV(kpi.data, `${kpi.label}.csv`)}> */}
                            <button
                              className="export-btn"
                              onClick={() => {
                                const flat = kpi.isCrosstab
                                  ? flattenCrosstab({
                                      rows: kpiData.rowValues,
                                      columns: kpiData.colValues,
                                      matrix: kpiData.table,
                                    })
                                  : kpiData;

                                exportXLSX(flat, `${kpi.label}.xlsx`);
                              }}
                            >
                              <i className="ri-file-text-line"></i>
                              <span className="export-text">CSV</span>
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    {/* <div className="kpi-preview">
                      {isNumeric ? (
                        <KpiTable data={kpiData} numericSummary />
                      ) : (
                        (() => {
                          const dynamicWidth = Math.max(900, (kpiData?.length || 1) * 120);

                          // If Crosstab, always show chart instead of table
                          if (kpi.isCrosstab) {
                            return (
                              <ChartRenderer
                                type={kpi.type}
                                data={kpiData}
                                width={`${dynamicWidth}px`}
                                height={400}
                              />
                            );
                          }

                          // Normal chart view
                          return kpi.viewMode === "chart" ? (
                            <ChartRenderer
                              type={kpi.type}
                              data={kpiData}
                              width={`${dynamicWidth}px`}
                              height={400}
                            />
                          ) : (
                            <KpiTable data={kpiData} />
                          );
                        })()
                      )}
                    </div> */}
                    {/* <div className="kpi-preview"> */}
                    <div id={`kpi-${kpi.id}`} className="kpi-preview">
                      {isNumeric ? (
                        <KpiTable data={kpiData} numericSummary />
                      ) : kpi.isCrosstab ? (
                        // Crosstab: show chart OR table based on viewMode
                        kpi.viewMode === "chart" ? (
                          <ChartRenderer
                            type={kpi.type || "stackedcolumn"}
                            data={kpiData}
                            width="100%"
                            height={400}
                            isCrosstab={true}
                          />
                        ) : (
                          // <CrosstabTable
                          //   data={{
                          //     rows: kpiData.rowValues,
                          //     columns: kpiData.colValues,
                          //     matrix: kpiData.table
                          //   }}
                          //   rowVar={kpiData.rowVar}
                          //   colVar={kpiData.colVar}
                          // />
                          <CrosstabTable
                            crosstab={crosstabDisplayData.crosstabData}
                          />
                        )
                      ) : kpi.viewMode === "chart" ? (
                        (() => {
                          const dynamicWidth = Math.max(
                            900,
                            (kpiData?.length || 1) * 120
                          );
                          return (
                            <ChartRenderer
                              type={kpi.type}
                              data={kpiData}
                              width={`${dynamicWidth}px`}
                              height={400}
                            />
                          );
                        })()
                      ) : (
                        <KpiTable data={kpiData} />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
